import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { getDeviceId } from '../../lib/deviceId';
import { fileToBase64 } from '../../lib/fileToBase64';
import { OFFENCE_CODES, rememberOffenceCode } from '../../lib/offenceCodes';
import type { Contravention, EvidenceKind, ParkingZone, PreIssuanceCheckResult } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { useNoticeBlock } from '../../lib/useNoticeBlock';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner } from '../../ui/components';

const REQUIRED_EVIDENCE: EvidenceKind[] = ['PLATE_CLOSEUP', 'VEHICLE_IN_CONTEXT'];
const OPTIONAL_EVIDENCE: EvidenceKind[] = ['SIGNAGE', 'WINDSCREEN', 'OTHER'];
const EVIDENCE_LABELS: Record<EvidenceKind, string> = {
  PLATE_CLOSEUP: 'Plate close-up',
  VEHICLE_IN_CONTEXT: 'Vehicle in context',
  SIGNAGE: 'Signage',
  WINDSCREEN: 'Windscreen',
  OTHER: 'Other',
};

const HIT_LABELS: Record<string, string> = {
  ACTIVE_SESSION: 'Active paid session',
  ACTIVE_PERMIT: 'Active permit',
  ACTIVE_EXEMPTION: 'Active exemption',
};

export function OfficerConsole() {
  const { persona } = useAuth();
  const officerId = persona?.officerRecord?.id;
  const agency = persona?.officerRecord?.agency;
  const { takeNoticeNumber } = useNoticeBlock(officerId);

  const { data: zonesPage, loading: zonesLoading, error: zonesError } = useApiQuery<{ items: ParkingZone[] }>('/api/v1/parking/zones');
  const zones = zonesPage?.items ?? [];

  const [zoneId, setZoneId] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [check, setCheck] = useState<PreIssuanceCheckResult | null>(null);
  const [checkedFor, setCheckedFor] = useState<{ zoneId: string; registrationNumber: string } | null>(null);
  const [firstObservedAt, setFirstObservedAt] = useState<Date | null>(null);

  const [offenceCode, setOffenceCode] = useState(OFFENCE_CODES[0]!.code);
  const [gpsLat, setGpsLat] = useState('');
  const [gpsLng, setGpsLng] = useState('');
  const [officerNotes, setOfficerNotes] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [files, setFiles] = useState<Partial<Record<EvidenceKind, File>>>({});
  const [clientUuid, setClientUuid] = useState<string>(() => crypto.randomUUID());

  const [submitting, setSubmitting] = useState(false);
  const [submitStage, setSubmitStage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [issued, setIssued] = useState<Contravention | null>(null);
  const [evidenceErrors, setEvidenceErrors] = useState<Partial<Record<EvidenceKind, string>>>({});

  const normalisedRegNo = registrationNumber.trim().toUpperCase();
  const staleCheck = !checkedFor || checkedFor.zoneId !== zoneId || checkedFor.registrationNumber !== normalisedRegNo;

  const runCheck = async () => {
    if (!zoneId || !normalisedRegNo) return;
    setChecking(true);
    setCheckError(null);
    try {
      const result = await api.get<PreIssuanceCheckResult>(
        `/api/v1/parking/officers/status-check?registrationNumber=${encodeURIComponent(normalisedRegNo)}&zoneId=${encodeURIComponent(zoneId)}`,
      );
      setCheck(result);
      setCheckedFor({ zoneId, registrationNumber: normalisedRegNo });
      setFirstObservedAt(new Date());
      setOverrideReason('');
    } catch (e) {
      setCheckError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Status check failed');
      setCheck(null);
      setCheckedFor(null);
    } finally {
      setChecking(false);
    }
  };

  const gpsLatNum = Number(gpsLat);
  const gpsLngNum = Number(gpsLng);
  const gpsValid = gpsLat.trim() !== '' && gpsLng.trim() !== '' && Number.isFinite(gpsLatNum) && Number.isFinite(gpsLngNum);

  const canSubmit =
    !!officerId &&
    !!agency &&
    !staleCheck &&
    !!check &&
    (!check.blocked || overrideReason.trim().length >= 3) &&
    !!offenceCode &&
    gpsValid &&
    !!files.PLATE_CLOSEUP &&
    !!files.VEHICLE_IN_CONTEXT &&
    !submitting;

  const resetForNextNotice = () => {
    setRegistrationNumber('');
    setCheck(null);
    setCheckedFor(null);
    setFirstObservedAt(null);
    setOverrideReason('');
    setOfficerNotes('');
    setGpsLat('');
    setGpsLng('');
    setFiles({});
    setClientUuid(crypto.randomUUID());
    setIssued(null);
    setSubmitError(null);
    setEvidenceErrors({});
  };

  const uploadEvidence = async (contraventionId: string, kind: EvidenceKind, file: File, capturedAtIso: string) => {
    const dataBase64 = await fileToBase64(file);
    await api.post(`/api/v1/parking/contraventions/${contraventionId}/evidence`, {
      kind,
      dataBase64,
      capturedAt: capturedAtIso,
      ...(gpsValid ? { gpsLat: gpsLatNum, gpsLng: gpsLngNum } : {}),
    });
  };

  const submit = async () => {
    if (!canSubmit || !officerId || !agency || !check) return;
    setSubmitting(true);
    setSubmitError(null);
    setEvidenceErrors({});
    const now = new Date();
    try {
      setSubmitStage('Allocating notice number…');
      const noticeNumber = await takeNoticeNumber(agency);

      setSubmitStage('Issuing contravention…');
      const created = await api.post<Contravention>('/api/v1/parking/contraventions', {
        noticeNumber,
        clientUuid,
        registrationNumber: normalisedRegNo,
        zoneId,
        offenceCode,
        officerId,
        deviceId: getDeviceId(),
        firstObservedAt: (firstObservedAt ?? now).toISOString(),
        issuedAt: now.toISOString(),
        gpsLat: gpsLatNum,
        gpsLng: gpsLngNum,
        officerNotes: officerNotes.trim() || undefined,
        overrideReason: check.blocked ? overrideReason.trim() : undefined,
      });
      rememberOffenceCode(created.id, offenceCode);
      setIssued(created);

      const errors: Partial<Record<EvidenceKind, string>> = {};
      for (const kind of [...REQUIRED_EVIDENCE, ...OPTIONAL_EVIDENCE]) {
        const file = files[kind];
        if (!file) continue;
        setSubmitStage(`Uploading ${EVIDENCE_LABELS[kind]}…`);
        try {
          await uploadEvidence(created.id, kind, file, now.toISOString());
        } catch (e) {
          errors[kind] = e instanceof ApiError ? e.message : 'Upload failed';
        }
      }
      setEvidenceErrors(errors);
    } catch (e) {
      setSubmitError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue contravention');
    } finally {
      setSubmitting(false);
      setSubmitStage(null);
    }
  };

  const retryEvidence = async (kind: EvidenceKind) => {
    const file = files[kind];
    if (!file || !issued) return;
    setEvidenceErrors((prev) => ({ ...prev, [kind]: undefined }) as Partial<Record<EvidenceKind, string>>);
    try {
      await uploadEvidence(issued.id, kind, file, new Date().toISOString());
      setEvidenceErrors((prev) => {
        const next = { ...prev };
        delete next[kind];
        return next;
      });
    } catch (e) {
      setEvidenceErrors((prev) => ({ ...prev, [kind]: e instanceof ApiError ? e.message : 'Upload failed' }));
    }
  };

  return (
    <div>
      <PageHeader
        title="Officer Console"
        subtitle="Plate + zone check, then issue a contravention on the spot."
      />

      {!officerId ? (
        <div className="mb-6">
          <InfoNote
            message={
              <>
                Your signed-in identity ({persona?.name}) isn't linked to a registered EnforcementOfficer profile in
                m46-parking, so you can browse and check status but can't issue a notice under your own badge. Sign in
                as one of the ENFORCEMENT_OFFICER personas, or (if you're a PARKING_ADMIN) register yourself via{' '}
                <Link to="/admin/officers" className="font-semibold underline">
                  Officer Registry
                </Link>
                .
              </>
            }
          />
        </div>
      ) : null}

      <div className="mb-6 card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">1. Plate &amp; zone status check</h2>
        {zonesError ? <ErrorNote message={zonesError} /> : null}
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field label="Zone">
            <select className="field-input" value={zoneId} onChange={(e) => setZoneId(e.target.value)} disabled={zonesLoading}>
              <option value="">{zonesLoading ? 'Loading zones…' : 'Choose a zone…'}</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.code} — {z.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Registration number">
            <input
              className="field-input uppercase"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              placeholder="e.g. S 12345"
            />
          </Field>
          <div className="flex items-end">
            <button className="btn-primary w-full sm:w-auto" onClick={runCheck} disabled={checking || !zoneId || !normalisedRegNo}>
              {checking ? 'Checking…' : 'Check'}
            </button>
          </div>
        </div>
        {checkError ? (
          <div className="mt-3">
            <ErrorNote message={checkError} />
          </div>
        ) : null}

        {check && !staleCheck ? (
          <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
            {check.blocked ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
                <div className="font-semibold">Blocked — a supervisor override reason is required to issue.</div>
                <ul className="mt-1 list-inside list-disc text-xs">
                  {check.blockingHits.map((h, i) => (
                    <li key={i}>{HIT_LABELS[h.type] ?? h.type}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">
                No active session, permit, or exemption found — clear to issue.
              </div>
            )}
            {check.priorNoticeInWindow ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800">
                Prior notice to this plate in this zone within the no-return window, issued {formatDateTime(check.priorNoticeInWindow.issuedAt)} —{' '}
                <Link className="font-semibold underline" to={`/contraventions/${check.priorNoticeInWindow.contraventionId}`}>
                  view it
                </Link>
                . Not a block, but the new notice will be flagged for supervisor review.
              </div>
            ) : null}
            <div className="text-xs text-slate-500">
              Operating hours right now:{' '}
              {check.withinOperatingHours === null ? 'unknown' : check.withinOperatingHours ? 'within hours' : 'outside hours (still lawful for some offence codes)'}
            </div>
          </div>
        ) : null}
        {checkedFor && staleCheck ? (
          <div className="mt-3">
            <InfoNote message="Zone or registration number changed since the last check — check again before issuing." />
          </div>
        ) : null}
      </div>

      {check && !staleCheck ? (
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">2. Issue contravention</h2>

          {issued ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <div className="font-bold">Notice {issued.noticeNumber} issued.</div>
                <div className="mt-1 text-xs">
                  {OFFENCE_CODES.find((o) => o.code === offenceCode)?.label} — {(issued.amountCents / 100).toFixed(2)} SCR
                </div>
              </div>
              {Object.entries(evidenceErrors).some(([, v]) => v) ? (
                <div className="space-y-2">
                  {(Object.entries(evidenceErrors) as [EvidenceKind, string | undefined][])
                    .filter(([, v]) => v)
                    .map(([kind, message]) => (
                      <div key={kind} className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                        <span>
                          {EVIDENCE_LABELS[kind]} failed to upload: {message}
                        </span>
                        <button className="btn-secondary !py-1" onClick={() => retryEvidence(kind)}>
                          Retry
                        </button>
                      </div>
                    ))}
                </div>
              ) : null}
              <div className="flex gap-2">
                <Link className="btn-primary" to={`/contraventions/${issued.id}`}>
                  View notice
                </Link>
                <button className="btn-secondary" onClick={resetForNextNotice}>
                  Issue another
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {submitError ? <ErrorNote message={submitError} /> : null}

              <Field label="Offence code">
                <select className="field-input" value={offenceCode} onChange={(e) => setOffenceCode(e.target.value)}>
                  {OFFENCE_CODES.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="GPS latitude" hint="Decimal degrees, e.g. -4.6195">
                  <input className="field-input" inputMode="decimal" value={gpsLat} onChange={(e) => setGpsLat(e.target.value)} placeholder="-4.6195" />
                </Field>
                <Field label="GPS longitude" hint="Decimal degrees, e.g. 55.4525">
                  <input className="field-input" inputMode="decimal" value={gpsLng} onChange={(e) => setGpsLng(e.target.value)} placeholder="55.4525" />
                </Field>
              </div>

              <div>
                <div className="field-label">Evidence photos</div>
                <p className="mb-2 text-[11px] text-slate-400">
                  Plate close-up and vehicle-in-context are required. Photos should stay well under 1.4&nbsp;MB — the evidence
                  endpoint's JSON body is capped at 2&nbsp;MB and base64 inflates file size by about a third.
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[...REQUIRED_EVIDENCE, ...OPTIONAL_EVIDENCE].map((kind) => (
                    <label key={kind} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs">
                      <span>
                        {EVIDENCE_LABELS[kind]}
                        {REQUIRED_EVIDENCE.includes(kind) ? <span className="text-rose-500"> *</span> : null}
                        {files[kind] ? <span className="ml-1 text-emerald-600">✓ {files[kind]!.name}</span> : null}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="max-w-[40%] text-[11px]"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          setFiles((prev) => ({ ...prev, [kind]: file }));
                        }}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <Field label="Officer notes" hint="Optional">
                <textarea className="field-input min-h-[4rem]" value={officerNotes} onChange={(e) => setOfficerNotes(e.target.value)} />
              </Field>

              {check.blocked ? (
                <Field label="Supervisor override reason" hint="Required — at least 3 characters — because this check came back blocked.">
                  <textarea
                    className="field-input min-h-[4rem]"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="e.g. Supervisor J. Barra authorised by phone — driver disputes session validity, issuing pending review."
                  />
                </Field>
              ) : null}

              <button className="btn-primary w-full" onClick={submit} disabled={!canSubmit}>
                {submitting ? (submitStage ?? 'Submitting…') : 'Issue contravention'}
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
